import React, { useEffect, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, Switch, FormControlLabel, TextField } from '@mui/material';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';


import FlightStatusCard   from '../components/FlightStatusCard';
import LocationCard       from '../components/LocationCard';
import QuickStatsBar      from '../components/QuickStatsBar';
import WeatherCardDashboard from '../components/WeatherCardDashboard';
import TFRCardDashboard from '../components/TFRCardDashboard';
import AirspaceCardDashboard from '../components/AirspaceCardDashboard';
import NearbyHazardsCardDashboard from '../components/NearbyHazardsCardDashboard';
import PlaceholderCard    from '../components/PlaceholderCard';
import DashboardMap from '../components/DashboardMap';
import useFlightCheck from '../hooks/useFlightCheck';

const hasSearch = (lat, lon) => lat != null && lon != null;

const SHOW_NEARBY_AIRCRAFT = false;
const PREF_COOKIE = 'flightiq_preferences';
const DEFAULT_PREFS = {
  temperatureUnit: 'F',
  distanceUnit: 'mi',
  altitudeUnit: 'ft',
  showMapOverlays: true,
};

function readPreferencesCookie() {
  if (typeof document === 'undefined') return DEFAULT_PREFS;
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${PREF_COOKIE}=`));
  if (!match) return DEFAULT_PREFS;

  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(decodeURIComponent(match.split('=')[1])) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePreferencesCookie(preferences) {
  if (typeof document === 'undefined') return;
  const value = encodeURIComponent(JSON.stringify(preferences));
  document.cookie = `${PREF_COOKIE}=${value}; Max-Age=31536000; Path=/; SameSite=Lax`;
}

export default function Home({ userLat, userLon, selectedDrone, onLocationSelect }) {
  
    const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState(readPreferencesCookie);
  const [mode, setMode] = useState('current');
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedDuration, setPlannedDuration] = useState(30);
  const [plannedAltitude, setPlannedAltitude] = useState(120);

  const updatePreference = (key, value) => {
    if (value === null || value === undefined) return;
    setPreferences(prev => {
      const next = { ...prev, [key]: value };
      writePreferencesCookie(next);
      return next;
    });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
	  };
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    const hasVisited = localStorage.getItem("hasVisited");
    if (!hasVisited) setShowWelcome(true);
  }, []);
  const handleAcknowledge = () => {
    localStorage.setItem("hasVisited", "true");
    setShowWelcome(false);
  };

  const [refreshCounter, setRefreshCounter] = useState(0);
  const searchStarted = hasSearch(userLat, userLon);
  const flightCheck = useFlightCheck({
    lat: userLat,
    lon: userLon,
    drone: selectedDrone,
    refreshTrigger: refreshCounter,
  });

  useEffect(() => {
    if (searchStarted) setRefreshCounter(c => c + 1);
  }, [userLat, userLon, searchStarted]);

  const isPlannedPrototype = mode === 'planned';
  const statusForCard = searchStarted ? flightCheck.status : 'unknown';
  const reasonForCard = searchStarted ? flightCheck.summary : '';
  const pendingChecks = searchStarted ? flightCheck.pendingChecks : [];

  return (
      <div>
      {/* This button will open the dialog */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          px: { xs: 1.5, md: 0 },
          py: 1,
        }}
      >
        <Button variant="outlined" onClick={handleClickOpen}>
          NOTICE
        </Button>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            mr: { xs: 0, md: 2 },
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: 'rgba(255,255,255,0.22)',
              px: 1.25,
              py: 0.4,
              fontWeight: 800,
            },
            '& .Mui-selected': {
              color: '#00e676',
              bgcolor: 'rgba(0,230,118,0.12) !important',
            },
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            Mode
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_event, value) => value && setMode(value)}
          >
            <ToggleButton value="current">Current</ToggleButton>
            <ToggleButton value="planned">Planned</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, ml: 0.75 }}>
            Units
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preferences.temperatureUnit}
            onChange={(_event, value) => updatePreference('temperatureUnit', value)}
          >
            <ToggleButton value="F">F</ToggleButton>
            <ToggleButton value="C">C</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preferences.distanceUnit}
            onChange={(_event, value) => updatePreference('distanceUnit', value)}
          >
            <ToggleButton value="mi">MI</ToggleButton>
            <ToggleButton value="km">KM</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preferences.altitudeUnit}
            onChange={(_event, value) => updatePreference('altitudeUnit', value)}
          >
            <ToggleButton value="ft">FT</ToggleButton>
            <ToggleButton value="m">M</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            sx={{ ml: 0.5, color: 'text.secondary', '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 700 } }}
            control={
              <Switch
                size="small"
                checked={preferences.showMapOverlays}
                onChange={event => updatePreference('showMapOverlays', event.target.checked)}
              />
            }
            label="Map overlays"
          />
        </Stack>
      </Box>

      {isPlannedPrototype && (
        <Box
          sx={{
            maxWidth: 1680,
            mx: 'auto',
            px: { xs: 1.5, md: 2 },
            pb: 1,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              p: 1,
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.04)',
            }}
          >
            <TextField
              size="small"
              type="datetime-local"
              label="Planned start"
              value={plannedStart}
              onChange={event => setPlannedStart(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              type="number"
              label="Duration"
              value={plannedDuration}
              onChange={event => setPlannedDuration(event.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              size="small"
              type="number"
              label="Altitude"
              value={plannedAltitude}
              onChange={event => setPlannedAltitude(event.target.value)}
              inputProps={{ min: 0, max: 400 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
              Forecast checks are staged here for the planned-flight phase; current legal and hazard checks still drive the status.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* The Dialog component itself */}
      <Dialog
        open={open}
        onClose={handleClose} // Allows closing by clicking the background
      >
        <DialogTitle>
          {"Welcome"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This website is currently in <strong>beta</strong>.  
            Data shown may not be 100% accurate.  
            Please always verify with official sources before flying.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* The "OK" button that closes the dialog */}
          <Button onClick={handleClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ px: { xs: 1.5, md: 2 }, pb: 2 }}>
        <Box
          sx={{
            maxWidth: 1680,
            mx: 'auto',
            pt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '380px minmax(0, 1fr)' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <Stack spacing={1} sx={{ minWidth: 0 }}>
              <FlightStatusCard
                status={statusForCard}
                reason={reasonForCard}
                reasons={flightCheck.reasons}
                loading={searchStarted && !flightCheck.hasAnyResult}
                pendingChecks={pendingChecks}
              />

              <LocationCard lat={userLat} lon={userLon} />

            {searchStarted && (
              <QuickStatsBar cardStatuses={flightCheck.cardStatuses} isReady={flightCheck.isComplete} />
            )}

            {searchStarted ? (
                <WeatherCardDashboard
                  lat={userLat}
                  lon={userLon}
                  drone={selectedDrone}
                  refreshTrigger={refreshCounter}
                  preferences={preferences}
                  onUpdate={() => {}}
                />
              ) : (
                <PlaceholderCard title="Weather" message="Set a location to load weather." />
              )}

              {searchStarted ? (
                <TFRCardDashboard
                  lat={userLat}
                  lon={userLon}
                  refreshTrigger={refreshCounter}
                  onUpdate={() => {}}
                />
              ) : (
                <PlaceholderCard title="Nearest TFRs" message="No location yet. Press GO." />
              )}

              {searchStarted ? (
                <AirspaceCardDashboard
                  lat={userLat}
                  lon={userLon}
                  refreshTrigger={refreshCounter}
                  onUpdate={() => {}}
                />
              ) : (
                <PlaceholderCard title="Airspace" message="Set a location to check airspace." />
              )}

              {searchStarted ? (
                <NearbyHazardsCardDashboard
                  lat={userLat}
                  lon={userLon}
                  refreshTrigger={refreshCounter}
                  onUpdate={() => {}}
                />
              ) : (
                <PlaceholderCard title="Nearby Hazards" message="Set a location to check for hazards." />
              )}

              {SHOW_NEARBY_AIRCRAFT && (
                <PlaceholderCard title="Nearby Aircraft" message="Live aircraft data is temporarily disabled." />
              )}
          </Stack>

          <DashboardMap
            lat={userLat}
            lon={userLon}
            onLocationSelect={onLocationSelect}
            preferences={preferences}
          />
        </Box>
      </Box>

      {showWelcome && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
            <h2 className="text-xl font-bold mb-4">Welcome to FlightIQ</h2>
            <p className="mb-4">
              This website is currently in beta and under development. Data shown may
              not be 100% accurate. Please always verify with official sources before flying.
			  Check the <strong>About</strong> page for more info.
            </p>
            <button
              onClick={handleAcknowledge}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              OK, I Understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
